import { useParams } from "react-router-dom";
import { useGetOneArticleQuery } from "../../redux/Service/api";
import { LoadingState } from "./LoadingState";
import { ErrorState } from "./ErrorState";
import { ShareButton } from "./ShareButton";

interface Section {
  title: string;
  content: string;
}

interface Article {
  title: string;
  date: string;
  description: string;
  photo: string;
  content: string;
  sections?: Section[];
}

function ArticleDetails() {
  const { id } = useParams();
  const { data: articleData, isLoading, isError } = useGetOneArticleQuery(id);
  const article = articleData?.data as Article;

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState />;

  return (
    <article className="min-h-screen bg-gray-50 py-8 md:py-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <header className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
            <span className="px-3 py-1 text-sm font-semibold text-purple-700 bg-purple-50 rounded-full w-fit">
              {article.title}
            </span>
            <time className="text-sm text-gray-500">{article.date}</time>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {article.description}
          </h1>
        </header>

        {/* Featured Image */}
        <div className="relative h-[250px] sm:h-[300px] md:h-[400px] lg:h-[500px] rounded-xl sm:rounded-2xl overflow-hidden mb-8 md:mb-12">
          <img
            src={article.photo}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
          <div className="grid gap-6 md:gap-8">
            {/* Content sections */}
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-sm">
              <p className="text-gray-700 leading-relaxed mb-6">
                {article.content}
              </p>

              {/* Additional content sections if available */}
              {article.sections?.map((section: Section, index: number) => (
                <div key={index} className="mb-6 md:mb-8 last:mb-0">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                    {section.title}
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>

            {/* Share buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-6 md:pt-8 border-t border-gray-200">
              <span className="text-gray-600 font-medium">مشاركة:</span>
              <ShareButton />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default ArticleDetails;
